import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import {
  StrategyConfig,
  StrategyValidationContext,
  CreateStrategyPayload,
  UpdateStrategyPayload,
} from '../types';
import {
  validateDynamicCriteria,
  hasUnfilledCriteriaPlaceholders,
} from '../../Admin/utils/strategyValidation';
import { transformCriteriaInputs } from './transformers';
import ScoutListConfiguration from '../../Admin/AddEditStrategy/components/StrategyTypes/ScoutListConfiguration';

/**
 * Configuration for Scout List strategy type
 *
 * Scout List uses AI-generated criteria to create targeted prospect lists.
 * It uses dynamic criteria templates and inputs.
 */
export const scoutListConfig: StrategyConfig = {
  type: 'SCOUT_LIST',
  label: 'Scout List',
  description:
    'Create a strategy using AI to generate targeted prospects based on your criteria',
  icon: faRobot,
  mode: 'target',
  disabled: false,

  ConfigComponent: ScoutListConfiguration,

  requiredFields: ['label', 'description', 'criteria_template'],

  validateProgress: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) {
      return !!(
        context.strategyDetails?.criteria_template ||
        context.strategyDetails?.codeSetId ||
        context.strategyDetails?.mode
      );
    }
    const template = context.dynamicCriteria?.criteria_template;
    // Check that template exists and all placeholders have valid data
    return !!template && !hasUnfilledCriteriaPlaceholders(context.dynamicCriteria);
  },

  validateComplete: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) return true;
    return validateDynamicCriteria(context.dynamicCriteria);
  },

  serializeForCreate: (
    context: StrategyValidationContext
  ): CreateStrategyPayload => {
    // Extract team IDs - teams may be stored as objects or IDs
    const teams = context.formData.teams?.map((team: number | { id: number }) =>
      typeof team === 'object' ? team.id : team
    );

    return {
      label: context.formData.label,
      description: context.formData.description,
      type: 'SCOUT_LIST',
      teams,
      refresh_event: context.formData.refresh_event,
      max_opportunities: context.formData.max_opportunities,
      criteria_template: context.dynamicCriteria?.criteria_template || '',
      criteria_inputs: transformCriteriaInputs(context.dynamicCriteria?.criteria_inputs),
    };
  },

  serializeForUpdate: (
    context: StrategyValidationContext
  ): UpdateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      refresh_event: context.formData.refresh_event,
      max_opportunities: context.formData.max_opportunities,
      criteria_template: context.dynamicCriteria?.criteria_template || '',
      criteria_inputs: transformCriteriaInputs(context.dynamicCriteria?.criteria_inputs),
    };
  },

  features: {
    supportsRefresh: true,
    hasMaxOpportunities: true,
  },
};
